/**
 * Stripe Service
 * 
 * Handles Stripe payment integration for Premium subscriptions.
 */

import backendClient from './backendClient';
import { openExternalUrl } from '../utils/capacitorBridge';

export const DEFAULT_STRIPE_RETURN_PATH = '/mymeals';
const STRIPE_RETURN_TO_KEY = '9meals_stripe_return_to';

const getWindowOrigin = () => {
  if (typeof window === 'undefined' || !window.location?.origin) {
    return 'http://localhost';
  }
  return window.location.origin;
};

const sanitizeStripeReturnPath = (rawPath) => {
  if (!rawPath || typeof rawPath !== 'string') return null;

  const trimmedPath = rawPath.trim();
  if (!trimmedPath) return null;

  // Prevent protocol-style and malformed paths.
  if (trimmedPath.startsWith('//')) return null;
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmedPath)) {
    try {
      const parsedUrl = new URL(trimmedPath);
      if (parsedUrl.origin !== getWindowOrigin()) return null;
      const localPath = `${parsedUrl.pathname}${parsedUrl.search || ''}${parsedUrl.hash || ''}`;
      return sanitizeStripeReturnPath(localPath);
    } catch {
      return null;
    }
  }

  if (!trimmedPath.startsWith('/')) return null;

  // Avoid loops to Stripe pages.
  if (trimmedPath === '/premium' || trimmedPath.startsWith('/premium?')) return null;
  if (trimmedPath === '/premium/success' || trimmedPath.startsWith('/premium/success?')) return null;

  return trimmedPath;
};

const getStoredStripeReturnPath = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  try {
    return window.sessionStorage.getItem(STRIPE_RETURN_TO_KEY);
  } catch {
    return null;
  }
};

const setStoredStripeReturnPath = (returnPath) => {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(STRIPE_RETURN_TO_KEY, returnPath);
  } catch {
    // Ignore storage exceptions in private contexts.
  }
};

const getReturnToFromCurrentSearch = () => {
  if (typeof window === 'undefined' || !window.location?.search) return null;
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get('returnTo');
};

export const getSafeStripeReturnPath = (returnPath) => {
  const directPath = sanitizeStripeReturnPath(returnPath);
  if (directPath) return directPath;

  const queryPath = sanitizeStripeReturnPath(getReturnToFromCurrentSearch());
  if (queryPath) return queryPath;

  const storedPath = sanitizeStripeReturnPath(getStoredStripeReturnPath());
  if (storedPath) return storedPath;

  return DEFAULT_STRIPE_RETURN_PATH;
};

const buildStripeReturnUrl = (pathname, returnPath) => {
  const safeReturnPath = getSafeStripeReturnPath(returnPath);
  const url = new URL(pathname, getWindowOrigin());
  url.searchParams.set('returnTo', safeReturnPath);
  return url.toString();
};

// Stripe Price IDs (these should match your Stripe Dashboard products)
export const STRIPE_PRICES = {
  YEARLY: 'price_yearly_premium',   // €4.99/month billed annually (€59.88/year)
  MONTHLY: 'price_monthly_premium', // €5.99/month
};

/**
 * Create a Stripe Checkout session for Premium subscription
 * @param {Object} options
 * @param {boolean} options.isYearly - Whether to create yearly or monthly subscription
 * @param {string} [options.returnPath] - In-app path to return to after Stripe
 * @returns {Promise<{url: string}>} - Checkout session URL
 */
export async function createCheckoutSession({ isYearly = true, returnPath }) {
  const priceId = isYearly ? STRIPE_PRICES.YEARLY : STRIPE_PRICES.MONTHLY;
  const safeReturnPath = getSafeStripeReturnPath(returnPath);
  setStoredStripeReturnPath(safeReturnPath);
  
  const response = await backendClient.post('/api/stripe/create-checkout-session', {
    priceId,
    successUrl: buildStripeReturnUrl('/premium/success', safeReturnPath),
    cancelUrl: buildStripeReturnUrl('/premium', safeReturnPath),
  }, { timeout: 10000 });
  
  return response;
}

/**
 * Redirect to Stripe Checkout
 * @param {Object} options
 * @param {boolean} options.isYearly - Whether to create yearly or monthly subscription
 * @param {string} [options.returnPath] - In-app path to return to after Stripe
 */
export async function redirectToCheckout({ isYearly = true, returnPath } = {}) {
  try {
    const { url } = await createCheckoutSession({
      isYearly,
      returnPath,
    });
    
    if (url) {
      await openExternalUrl(url);
    } else {
      throw new Error('No checkout URL received');
    }
  } catch (error) {
    console.error('[StripeService] Error creating checkout session:', error);
    throw error;
  }
}

/**
 * Get customer portal URL for managing subscription
 * @param {Object} options
 * @param {string} [options.returnPath] - In-app path to return to after leaving profile
 * @returns {Promise<{url: string}>}
 */
export async function getCustomerPortalUrl({ returnPath } = {}) {
  const safeReturnPath = getSafeStripeReturnPath(returnPath);
  setStoredStripeReturnPath(safeReturnPath);

  const response = await backendClient.post('/api/stripe/customer-portal', {
    returnUrl: buildStripeReturnUrl('/myprofile', safeReturnPath),
  }, { timeout: 10000 });
  
  return response;
}

/**
 * Check subscription status
 * @returns {Promise<{isPremium: boolean, subscription: Object|null}>}
 */
export async function checkSubscriptionStatus() {
  const response = await backendClient.get('/api/stripe/subscription-status', { timeout: 10000 });
  return response;
}

const stripeService = {
  createCheckoutSession,
  redirectToCheckout,
  getCustomerPortalUrl,
  checkSubscriptionStatus,
  getSafeStripeReturnPath,
  DEFAULT_STRIPE_RETURN_PATH,
  STRIPE_PRICES,
};

export default stripeService;
